import React, { memo, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ShareButton from './ShareButton';
import * as FiIcons from 'react-icons/fi';

const { 
  FiMapPin, FiThermometer, FiDroplet, FiWind, FiAlertTriangle, 
  FiEye, FiSun, FiUmbrella, FiShield, FiCalendar, FiClock, FiUsers, FiActivity 
} = FiIcons;

const IliganBarangaysWeather = memo(({ data, typhoonData, outlook }) => {
  const barangays = [
    { name: 'Poblacion', district: 'City Proper', population: '15,000+', icon: FiMapPin, color: 'from-blue-500 to-blue-600', riskFactors: ['urban flooding', 'traffic'], elevation: 'Sea level' },
    { name: 'Tubod', district: 'Eastern', population: '25,000+', icon: FiUsers, color: 'from-green-500 to-green-600', riskFactors: ['river flooding', 'Mandulog overflow'], elevation: '50-100m' },
    { name: 'Saray', district: 'Northern', population: '20,000+', icon: FiWind, color: 'from-purple-500 to-purple-600', riskFactors: ['landslide', 'runoff'], elevation: '100-200m' },
    { name: 'Santiago', district: 'Western', population: '18,000+', icon: FiSun, color: 'from-orange-500 to-orange-600', riskFactors: ['coastal surge', 'seawall breach'], elevation: 'Sea level' },
    { name: 'Pala-o', district: 'Southern', population: '22,000+', icon: FiDroplet, color: 'from-teal-500 to-teal-600', riskFactors: ['river junction', 'debris flow'], elevation: '20-80m' },
    { name: 'Tipanoy', district: 'Industrial', population: '12,000+', icon: FiShield, color: 'from-red-500 to-red-600', riskFactors: ['industrial surge', 'runoff'], elevation: 'Sea level' }
  ];

  // Helper to generate SEEDED random numbers so data stays consistent for the current hour
  const seededRandom = (seed) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  const hourlyForecast = useMemo(() => {
    const now = new Date();
    const currentBaseHour = now.getHours();
    const currentDay = now.getDate();
    const hours = [];
    
    const hourlyPatterns = [
      { temp: [24, 25, 24, 23, 23, 24], condition: 'Clear', rain: 5, wind: 8, emoji: '🌙' },
      { temp: [25, 26, 27, 28, 29, 30], condition: 'Sunny', rain: 10, wind: 12, emoji: '🌅' },
      { temp: [31, 32, 32, 31, 30, 29], condition: 'Partly Cloudy', rain: 25, wind: 15, emoji: '☀️' },
      { temp: [28, 27, 26, 25, 25, 24], condition: 'Cloudy', rain: 40, wind: 10, emoji: '🌤️' }
    ];

    for (let i = 0; i < 24; i++) {
      const hourTime = new Date(now);
      hourTime.setHours(currentBaseHour + i);
      const hour = hourTime.getHours();
      
      // Use hour and day as seed for consistency
      const seed = (currentDay * 100) + hour;
      const rand = seededRandom(seed);

      let patternIndex = 0;
      if (hour >= 6 && hour < 12) patternIndex = 1;
      else if (hour >= 12 && hour < 18) patternIndex = 2;
      else if (hour >= 18 && hour < 24) patternIndex = 3;

      const pattern = hourlyPatterns[patternIndex];
      const subHour = hour % 6;
      
      let adjustedTemp = pattern.temp[subHour] + Math.floor(rand * 3) - 1;
      let adjustedRain = Math.max(0, pattern.rain + Math.floor(rand * 20) - 10);
      let adjustedWind = Math.max(5, pattern.wind + Math.floor(rand * 8) - 4);

      if (typhoonData) {
        adjustedTemp -= 3;
        adjustedRain = Math.min(95, adjustedRain + 40);
        adjustedWind = Math.min(80, adjustedWind + 25);
      }

      hours.push({
        time: hourTime.toLocaleTimeString('en-PH', { hour: 'numeric', hour12: true }),
        hour24: hour,
        temperature: adjustedTemp,
        condition: pattern.condition,
        rainChance: adjustedRain,
        windSpeed: adjustedWind,
        weatherEmoji: adjustedRain > 60 ? '🌧️' : (hour >= 18 || hour < 6 ? '🌙' : pattern.emoji),
        isPeakHours: hour >= 11 && hour <= 15
      });
    }
    return hours;
  }, [typhoonData]); // Only recalculate if typhoon status changes

  const getBarangayWeather = (name) => {
    const base = data?.mindanao || { condition: 'Partly cloudy', temperature: '26-31°C', rainfall: 'Light' };
    return { ...base, floodRisk: name === 'Tubod' || name === 'Pala-o' ? 'High' : 'Medium' };
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      {/* Barangays Grid */}
      <section className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <SafeIcon icon={FiMapPin} className="mr-2 text-blue-600" /> Iligan Barangay Conditions
          </h2>
          <ShareButton size="small" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {barangays.map((brgy) => {
            const weather = getBarangayWeather(brgy.name);
            return (
              <div key={brgy.name} className="p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${brgy.color} text-white`}>
                    <SafeIcon icon={brgy.icon} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">Brgy. {brgy.name}</h3>
                    <p className="text-xs text-gray-500">{brgy.district}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center"><SafeIcon icon={FiThermometer} className="mr-1 text-red-400" /> {weather.temperature}</div>
                  <div className="flex items-center"><SafeIcon icon={FiDroplet} className="mr-1 text-blue-400" /> Risk: {weather.floodRisk}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 24-Hour Forecast - Balanced Layout */}
      <section className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <SafeIcon icon={FiClock} className="mr-2 text-orange-500" /> 24-Hour Timeline
          </h2>
          <div className="text-xs text-gray-400 italic">Consistent for the current hour</div>
        </div>

        <div className="overflow-x-auto pb-4 mb-8 -mx-2 px-2 scrollbar-hide">
          <div className="flex space-x-4 min-w-max">
            {hourlyForecast.map((hour, idx) => (
              <div key={idx} className={`w-24 p-3 rounded-xl border text-center ${idx === 0 ? 'bg-blue-600 text-white border-blue-700 shadow-lg' : 'bg-gray-50 border-gray-100'}`}>
                <div className="text-[10px] uppercase font-bold opacity-80 mb-1">{hour.time}</div>
                <div className="text-2xl my-2">{hour.weatherEmoji}</div>
                <div className="font-bold">{hour.temperature}°C</div>
                <div className="text-[10px] mt-1 opacity-70">{hour.rainChance}% rain</div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Blocks */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: 'Morning', range: '6AM-12PM', color: 'bg-yellow-50 text-yellow-800', icon: FiSun },
            { label: 'Afternoon', range: '12PM-6PM', color: 'bg-orange-50 text-orange-800', icon: FiActivity },
            { label: 'Evening', range: '6PM-12AM', color: 'bg-indigo-50 text-indigo-800', icon: FiClock }
          ].map((period) => (
            <div key={period.label} className={`${period.color} p-4 rounded-xl border border-current/10 flex items-center space-x-4`}>
              <div className="p-2 bg-white/50 rounded-lg"><SafeIcon icon={period.icon} /></div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider">{period.label}</div>
                <div className="text-sm font-medium opacity-90">{period.range}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </motion.div>
  );
});

IliganBarangaysWeather.displayName = 'IliganBarangaysWeather';
export default IliganBarangaysWeather;